function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasContent(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export function deepMerge(base, override) {
  if (!isPlainObject(base)) return override ?? base;
  if (!isPlainObject(override)) return base;

  const result = { ...base };

  for (const [key, overrideValue] of Object.entries(override)) {
    if (overrideValue === null || overrideValue === undefined) continue;

    const baseValue = base[key];

    if (Array.isArray(overrideValue)) {
      result[key] = overrideValue.length > 0 ? overrideValue : (baseValue ?? overrideValue);
      continue;
    }

    if (isPlainObject(overrideValue) && isPlainObject(baseValue)) {
      result[key] = deepMerge(baseValue, overrideValue);
      continue;
    }

    if (hasContent(overrideValue)) {
      result[key] = overrideValue;
    }
  }

  return result;
}

function normalizeWhyItMatters(section = {}) {
  const normalized = { ...section };

  if (!normalized.description && normalized.desc) {
    normalized.description = normalized.desc;
  }

  if (!hasContent(normalized.checks) && hasContent(normalized.points)) {
    normalized.checks = normalized.points.map((point) => {
      if (typeof point === 'string') return point;
      return point?.title || point?.desc || '';
    }).filter(Boolean);
  }

  if (!hasContent(normalized.cards) && hasContent(normalized.points)) {
    normalized.cards = normalized.points.map((point) => {
      if (typeof point === 'string') return { title: point, desc: '' };
      return {
        title: point?.title || '',
        desc: point?.desc || point?.description || '',
      };
    }).filter((card) => card.title || card.desc);
  }

  return normalized;
}

function normalizeSplit(section = {}, staticSplit = {}) {
  const normalized = deepMerge(staticSplit, section);

  if (!normalized.title && normalized.heading) {
    normalized.title = normalized.heading;
  }

  if (!normalized.description && hasContent(normalized.paragraphs)) {
    normalized.description = normalized.paragraphs.join('\n\n');
  }

  return normalized;
}

function normalizeWhyUs(section = {}, staticWhyUs = {}) {
  const normalized = deepMerge(staticWhyUs, section);

  if (!normalized.description && normalized.desc) {
    normalized.description = normalized.desc;
  }

  if (!hasContent(normalized.checks) && hasContent(normalized.cards)) {
    normalized.checks = normalized.cards.map((card) => {
      if (typeof card === 'string') return card;
      return card?.title || card?.desc || '';
    }).filter(Boolean);
  }

  return normalized;
}

function normalizeCta(section = {}, staticCta = {}) {
  const normalized = deepMerge(staticCta, section);

  if (!normalized.title && normalized.heading) {
    normalized.title = normalized.heading;
  }

  if (!normalized.desc && (normalized.paragraph || normalized.text)) {
    normalized.desc = normalized.paragraph || normalized.text;
  }

  if (!normalized.btnText && normalized.primaryText) {
    normalized.btnText = normalized.primaryText;
  }

  if (!normalized.btnLink && normalized.primaryLink) {
    normalized.btnLink = normalized.primaryLink;
  }

  return normalized;
}

function normalizeCardsSection(section = {}, staticSection = {}) {
  const normalized = deepMerge(staticSection, section);

  if (!normalized.description && normalized.desc) {
    normalized.description = normalized.desc;
  }

  if (!hasContent(normalized.cards) && hasContent(normalized.points)) {
    normalized.cards = normalized.points;
  }

  return normalized;
}

export function mergeServiceDetail(staticDetail = {}, sanityDetail = {}) {
  const merged = deepMerge(staticDetail, sanityDetail);

  if (merged.whyItMatters) {
    merged.whyItMatters = normalizeWhyItMatters(
      deepMerge(staticDetail.whyItMatters, merged.whyItMatters)
    );
  }

  if (merged.split) {
    merged.split = normalizeSplit(merged.split, staticDetail.split);
  }

  if (merged.whyUs) {
    merged.whyUs = normalizeWhyUs(merged.whyUs, staticDetail.whyUs);
  }

  if (merged.cta) {
    merged.cta = normalizeCta(merged.cta, staticDetail.cta);
  }

  if (merged.ourServices) {
    merged.ourServices = normalizeCardsSection(merged.ourServices, staticDetail.ourServices);
  }

  if (merged.process) {
    merged.process = normalizeCardsSection(merged.process, staticDetail.process);
  }

  if (merged.roi) {
    merged.roi = normalizeWhyUs(merged.roi, staticDetail.roi);
  }

  if (merged.metrics) {
    merged.metrics = normalizeCardsSection(merged.metrics, staticDetail.metrics);
  }

  if (merged.sectors) {
    merged.sectors = deepMerge(staticDetail.sectors, merged.sectors);
  }

  if (merged.coverage) {
    merged.coverage = deepMerge(staticDetail.coverage, merged.coverage);
  }

  if (merged.splitAudit) {
    merged.splitAudit = deepMerge(staticDetail.splitAudit, merged.splitAudit);
  }

  if (merged.splitValue) {
    merged.splitValue = deepMerge(staticDetail.splitValue, merged.splitValue);
  }

  return merged;
}

const PORTFOLIO_LINK_ALIASES = {
  '/portfolio/3d-event-design': '/portfolio/3d-event-exhibition',
  '/portfolio/digital-marketing': '/portfolio/social-media-marketing',
  '/portfolio/seo-ai-solutions': '/portfolio/seo',
};

export function normalizePortfolioLink(link) {
  return PORTFOLIO_LINK_ALIASES[link] || link;
}

export function mergePortfolioItem(staticItem = {}, sanityItem = {}) {
  const merged = deepMerge(staticItem, sanityItem);
  merged.link = normalizePortfolioLink(merged.link);

  if (Array.isArray(merged.gallery)) {
    merged.gallery = merged.gallery.map(g => {
      if (!g) return g;
      const cleanImages = (g.images || []).filter(Boolean);
      return {
        ...g,
        images: cleanImages.length > 0 ? cleanImages : undefined
      };
    });
  }

  if (!hasContent(merged.gallery) && hasContent(staticItem.gallery)) {
    merged.gallery = staticItem.gallery;
  }

  return merged;
}
